import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { areaOpacity, cacheStatusColors, cacheStatusOrder, TOP_URLS_RESPONSE_TIME_SUM } from '../graph/graph';
import { formatDuration, upperFirstLetter } from '@shared/utils/data-utils';
import { AuthService } from '@shared/services/auth.service';
import {
  ActionEnum,
  Rps,
  SiteBandwidth,
  SiteTrafficBreakdown,
  UrlBandwidth,
  UrlResponseCodesCount,
  UrlStats,
} from '@shared/services/logs/logs.service';
import * as Highcharts from 'highcharts/highcharts';
import { TooltipPositionerPointObject, XAxisOptions, YAxisOptions } from 'highcharts/highcharts';
import { SizeFormatService } from '@shared/utils/size-format.service';
import { toSize } from '@shared/utils/size';

type ChartOptions = Highcharts.Options & {
  xAxis: XAxisOptions;
  yAxis: YAxisOptions;
};

// There is a bug in HighCharts which applies bar.opacity twice, hence th square root
const barOpacity = Math.sqrt(areaOpacity);

function padToSlots<T>(data: T[]): (T | undefined)[] {
  return data.length > 0 ? Array.from({ length: 5 }, (_, i) => data[i]) : [];
}
const baseChart: ChartOptions = {
  accessibility: {
    enabled: false,
  },
  title: {
    style: {
      color: '#434343',
      fontSize: '15px',
    },
    y: -15,
  },
  chart: {
    animation: false,
    height: 400,
    type: 'bar',
    spacing: [40, 50, 20, 10],
  },
  xAxis: {
    labels: {
      style: {
        cursor: 'pointer',
        fontSize: '0.7em',
      },
    },
  },
  yAxis: {
    title: {
      text: null,
    },
  },
  tooltip: {
    outside: true,
    positioner: function (labelWidth, labelHeight, point: TooltipPositionerPointObject) {
      return {
        x: point.plotX + point.shapeArgs['width'] / 2 - labelWidth / 2,
        y: point.plotY - labelHeight - 5,
      };
    },
  },
  legend: {
    itemStyle: {
      fontSize: '0.7em',
      textOverflow: 'ellipsis',
    },
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    bar: {
      borderRadius: '10%',
      groupPadding: 0.1,
      pointPadding: 0,
    },
    series: {
      animation: false,
      stacking: null,
      events: { click: null },
    },
  },
  exporting: {
    chartOptions: {
      chart: {
        backgroundColor: '#FFFFFF',
      },
    },
    buttons: {
      contextButton: {
        align: 'right',
        x: 30,
        y: -36,
        menuItems: [
          'printChart',
          'separator',
          'downloadPNG',
          'downloadJPEG',
          'downloadSVG',
          'separator',
          'downloadCSV',
          'downloadXLS',
        ],
      },
    },
  },
};
export const baseCssXAxis =
  'cursor: pointer; text-overflow: ellipsis; word-wrap: break-word; word-break; overflow: hidden; white-space: nowrap;';

function legendBelowBar() {
  return `<div align="center" title="${this.value}" style="${baseCssXAxis}; text-decoration: underline; break-all; width: 460px; margin-left: 5px; margin-top: 10px;">${this.value}</div>`;
}

@Injectable()
export class Bar {
  constructor(
    private translate: TranslateService,
    private auth: AuthService,
    private sizeFormatService: SizeFormatService,
  ) {}

  getBaseChart() {
    return JSON.parse(JSON.stringify(baseChart));
  }

  formatDataTrafic(data: (SiteTrafficBreakdown & Rps)[], titleKey: string): ChartOptions {
    const padded = padToSlots(data);
    const rpsData = padded.map((d) => d?.rps ?? null);
    let { xAxis, yAxis, tooltip, title, plotOptions, ...restBaseChart } = this.getBaseChart();
    xAxis.categories = padded.map((d) => d?.site ?? '');

    title.text = this.translate.instant(titleKey);
    yAxis = [
      {
        opposite: true,
        labels: {
          style: {
            fontSize: '0.7em',
          },
        },
        title: {
          text: this.translate.instant('requests'),
        },
      },
      {
        labels: {
          style: {
            color: '#FF5370',
            fontSize: '0.7em',
          },
        },
        title: {
          text: this.translate.instant('BlockedSuspicious'),
          style: {
            color: '#FF3357FF',
          },
        },
      },
    ];
    tooltip.formatter = function () {
      if (this.series?.userOptions?.custom?.slug === 'total') {
        return `${this.series.name} : <b>${this.y}</b>, RPS : <b>${rpsData[this.point.index]}</b>`;
      } else {
        return `${this.series.name} : <b>${this.y}</b>`;
      }
    };
    return {
      ...restBaseChart,
      xAxis,
      yAxis,
      tooltip,
      title,
      plotOptions,
      series: [
        {
          type: 'bar',
          name: this.translate.instant('requests'),
          data: padded.map((d) => d?.total ?? null),
          color: '#c3c3c3',
          opacity: barOpacity,
          yAxis: 0,
          legendIndex: 0,
        },
        {
          type: 'bar',
          name: this.translate.instant('suspiciouses'),
          custom: {
            slug: ActionEnum.SUSPICIOUS,
          },
          data: padded.map((d) => d?.suspect ?? null),
          color: '#f7a35c',
          yAxis: 1,
          legendIndex: 2,
        },
        {
          type: 'bar',
          name: this.translate.instant('Blocked'),
          custom: {
            slug: ActionEnum.BLOCKED,
          },
          data: padded.map((d) => d?.blocked ?? null),
          color: '#ff5370',
          yAxis: 1,
          legendIndex: 1,
        },
        {
          type: 'bar',
          name: this.translate.instant('RPS'),
          custom: {
            slug: 'rps',
          },
          data: rpsData,
          color: '#ff5370',
          visible: false,
          showInLegend: false,
        },
      ],
    };
  }

  formatDataResponseTime(data: UrlStats[]): Highcharts.Options {
    const lang = this.auth.getCurrentLanguage();

    const padded = padToSlots(data);
    const baseChart = this.getUrlBaseChart(padded);
    let { xAxis, tooltip, title, plotOptions, yAxis, chart, ...restBaseChart } = baseChart;
    chart.type = 'columnrange';
    chart.inverted = true;
    (baseChart.yAxis as YAxisOptions).labels = {
      formatter: function () {
        return formatDurationAxis(this.value as number, lang);
      },
    };
    yAxis.title = {
      text: this.translate.instant('ResponseTime'),
    };
    plotOptions.columnrange = {
      borderRadius: '50%',
      pointWidth: 15,
    };
    title.text = this.translate.instant('SlowestURLs');
    tooltip.formatter = function () {
      if (
        typeof this.low !== 'undefined' &&
        this.low !== null &&
        typeof this.high !== 'undefined' &&
        this.high !== null
      ) {
        return `<b>Min</b> : <b>${formatDuration(this.low, lang)}</b>, <b>Max</b> : <b>${formatDuration(this.high, lang)}</b>`;
      } else {
        return `<b>${this.series.name}</b> : <b>${formatDuration(this.y, lang)}</b>`;
      }
    };

    return {
      ...restBaseChart,
      chart,
      xAxis,
      yAxis,
      tooltip,
      title,
      plotOptions,
      series: [
        {
          type: 'columnrange',
          name: this.translate.instant('MinMaxInterval'),
          custom: {
            slug: 'minMax',
          },
          data: padded.map((d) => (d ? [Math.round(d.min), Math.round(d.max)] : null)),
          color: '#088cff',
        },
        {
          type: 'scatter',
          color: '#4099ff',
          marker: {
            symbol: 'circle',
            lineColor: '#FFFFFF',
            lineWidth: 1,
          },
          data: padded.map((d) => d?.avg ?? null),
          name: this.translate.instant('Avg'),
        },
      ],
    };
  }

  formatDataResponsesCodes(data: UrlResponseCodesCount[]): Highcharts.Options {
    const padded = padToSlots(data);
    let { xAxis, yAxis, title, plotOptions, ...restBaseChart } = this.getUrlBaseChart(padded);
    plotOptions.series.stacking = 'normal';
    title.text = this.translate.instant('TopErrorURLs');
    return {
      ...restBaseChart,
      xAxis,
      yAxis,
      plotOptions,
      title,
      series: [
        {
          type: 'bar',
          name: this.translate.instant('5XX'),
          custom: {
            slug: '5XX',
          },
          data: padded.map((d) => d?.count5xx ?? null),
          color: '#ff5370',
          index: 1,
          legendIndex: 1,
        },
        {
          type: 'bar',
          name: this.translate.instant('4XX'),
          custom: {
            slug: '4XX',
          },
          data: padded.map((d) => d?.count4xx ?? null),
          color: '#f7a35c',
          index: 2,
          legendIndex: 0,
        },
      ],
    };
  }

  formatDataResponseTimeSum(data: UrlStats[]): Highcharts.Options {
    const lang = this.auth.getCurrentLanguage();

    const padded = padToSlots(data);
    let { xAxis, yAxis, tooltip, title, plotOptions, ...restBaseChart } = this.getUrlBaseChart(padded);
    yAxis.labels = {
      formatter: function () {
        return formatDurationAxis(this.value as number, lang);
      },
    };
    yAxis.title = {
      text: this.translate.instant('CumulativeResponseTime'),
    };
    plotOptions.series.stacking = 'normal';
    title.text = this.translate.instant('TopCpuConsumingURLs');
    tooltip.formatter = function () {
      return `<b>${this.series.name}</b> : <b>${formatDuration(this.y, lang)}</b>`;
    };

    return {
      ...restBaseChart,
      xAxis,
      yAxis,
      plotOptions,
      tooltip,
      title,
      series: [
        {
          type: 'bar',
          name: `${this.translate.instant('Graph.seriesName.responseTimeSum')}`,
          custom: {
            slug: TOP_URLS_RESPONSE_TIME_SUM,
          },
          data: padded.map((d) => d?.sum ?? null),
          color: 'green',
        },
      ],
    };
  }

  formatDataBandwidth(data: UrlBandwidth[]): Highcharts.Options {
    const padded = padToSlots(data);
    let { xAxis, yAxis, tooltip, title, plotOptions, ...restBaseChart } = this.getUrlBaseChart(padded);
    const bytes = this.translate.instant('bytes');
    yAxis.title = { text: upperFirstLetter(bytes) };
    plotOptions.series.stacking = 'normal';
    plotOptions.bar.opacity = barOpacity;
    title.text = this.translate.instant('TopBandwidthURLs');
    const formatSize = (size) => this.sizeFormatService.formatSize(toSize(size));
    tooltip.formatter = function () {
      return `<b>${this.series.name}</b> : <b>${formatSize(this.y)}</b>`;
    };

    return {
      ...restBaseChart,
      xAxis,
      yAxis,
      plotOptions,
      tooltip,
      title,
      series: cacheStatusOrder.map((cacheStatus) => ({
        type: 'bar',
        name: `${this.translate.instant(`CacheStatus.${cacheStatus}`)}`,
        custom: { slug: cacheStatus },
        data: padded.map((d) => d?.bandwidth?.[cacheStatus] ?? null),
        color: cacheStatusColors[cacheStatus],
      })),
    };
  }

  formatTopSitesByBandwidthBreakdown(data: SiteBandwidth[]): Highcharts.Options {
    let { xAxis, yAxis, tooltip, title, plotOptions, ...restBaseChart } = this.getBaseChart();
    const bytes = this.translate.instant('bytes');
    yAxis.title = { text: upperFirstLetter(bytes) };
    plotOptions.series.stacking = 'normal';
    plotOptions.bar.opacity = barOpacity;
    title.text = this.translate.instant('Graph.breakdown.topSitesByBandwidth.title');
    const formatSize = (size) => this.sizeFormatService.formatSize(toSize(size));
    tooltip.formatter = function () {
      return `<b>${this.series.name}</b> : <b>${formatSize(this.y)}</b>`;
    };

    const padded = padToSlots(data);
    xAxis.categories = padded.map((d) => d?.site ?? '');

    return {
      ...restBaseChart,
      xAxis,
      yAxis,
      plotOptions,
      tooltip,
      title,
      series: cacheStatusOrder.map((cacheStatus) => ({
        type: 'bar',
        name: `${this.translate.instant(`CacheStatus.${cacheStatus}`)}`,
        custom: { slug: cacheStatus },
        data: padded.map((d) => d?.bandwidth?.[cacheStatus] ?? null),
        color: cacheStatusColors[cacheStatus],
      })),
    };
  }

  private getUrlBaseChart(data: ({ key?: string; url: string } | undefined)[]): ChartOptions {
    const chart = this.getBaseChart();
    chart.xAxis.categories = data.map((d) => d?.key ?? d?.url ?? '');
    chart.xAxis.labels = {
      align: 'left',
      x: 0,
      y: -24,
      useHTML: true,
      style: {
        width: '',
      },
      formatter: legendBelowBar,
    };
    chart.plotOptions.bar.pointWidth = 20;
    return chart;
  }
}

export function formatDurationAxis(ms: number, lang: string): string {
  if (!ms || isNaN(ms)) {
    return '';
  }

  if (ms < 1) {
    return ms * 1000 + 'µs';
  } else if (ms < 1000) {
    return Math.round(ms) + 'ms';
  } else {
    return showSeconds(ms / 1000, lang);
  }
}

function showSeconds(value: number, lang: string): string {
  return new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(value) + 's';
}
